import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import AOS from "aos";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProcessingLoader from "@/components/ProcessingLoader";

import Vacancies from "@/pages/Vacancies"; // eager: this is the homepage ("/")
import SeoHead from "@/components/SeoHead";

// Route-based code splitting: every non-home page ships as its own chunk so the
// initial load stays small (big PageSpeed win, esp. the heavy admin panel).
const Home = lazy(() => import("@/pages/Home"));
const ThemePreview = lazy(() => import("@/pages/ThemePreview"));
const About = lazy(() => import("@/pages/About"));
const Services = lazy(() => import("@/pages/Services"));
const Enquiry = lazy(() => import("@/pages/Enquiry"));
const Contact = lazy(() => import("@/pages/Contact"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Downloads = lazy(() => import("@/pages/Downloads"));
const Notices = lazy(() => import("@/pages/Notices"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const VacancyDetail = lazy(() => import("@/pages/VacancyDetail"));
const Blogs = lazy(() => import("@/pages/Blogs"));
const BlogDetail = lazy(() => import("@/pages/BlogDetail"));

// Admin panel — hidden from public nav, protected by AdminLayout. All lazy.
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminHome = lazy(() => import("@/pages/admin/AdminHome"));
const AdminSite = lazy(() => import("@/pages/admin/AdminSite"));
const AdminSEO = lazy(() => import("@/pages/admin/AdminSEO"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminVacancies = lazy(() => import("@/pages/admin/AdminVacancies"));
const AdminJobSEO = lazy(() => import("@/pages/admin/AdminJobSEO"));
const AdminBlogs = lazy(() => import("@/pages/admin/AdminBlogs"));
const AdminResumes = lazy(() => import("@/pages/admin/AdminResumes"));
const AdminIntegrations = lazy(() => import("@/pages/admin/AdminIntegrations"));
const AdminSlides = lazy(() => import("@/pages/admin/AdminSlides"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminSearchAnalytics = lazy(() => import("@/pages/admin/AdminSearchAnalytics"));
const AdminNewUpdates = lazy(() => import("@/pages/admin/AdminNewUpdates"));
import { ThemeProvider } from "@/context/ThemeContext";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading">
    <div className="h-8 w-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
  </div>
);

function AppRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<Vacancies />} />
      <Route path="/theme-preview" element={<ThemePreview />} />
      <Route path="/solar" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/services" element={<Services />} />
      <Route path="/enquiry" element={<Enquiry />} />
      <Route path="/solar/apply" element={<Navigate to="/enquiry" replace />} />
      <Route path="/loan/apply" element={<Navigate to="/enquiry?service=Solar%20Financing%20Information" replace />} />
      <Route path="/csc" element={<Navigate to="/services" replace />} />
      <Route path="/csc/apply" element={<Navigate to="/enquiry" replace />} />
      <Route path="/irrigation" element={<Navigate to="/services" replace />} />
      <Route path="/irrigation/apply" element={<Navigate to="/enquiry" replace />} />
      <Route path="/vacancies" element={<Navigate to="/" replace />} />
      <Route path="/vacancies/:id" element={<VacancyDetail />} />
      <Route path="/blogs" element={<Blogs />} />
      <Route path="/blogs/:slug" element={<BlogDetail />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/notices" element={<Notices />} />
      <Route path="/downloads" element={<Downloads />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/login" element={<Navigate to="/enquiry" replace />} />
      <Route path="/register" element={<Navigate to="/enquiry" replace />} />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/status" element={<Navigate to="/contact" replace />} />

      {/* ─── Admin panel (hidden from public nav) ─── */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminHome />} />
        <Route path="seo" element={<AdminSEO />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="vacancies" element={<AdminVacancies />} />
        <Route path="new-updates" element={<AdminNewUpdates />} />
        <Route path="job-seo" element={<AdminJobSEO />} />
        <Route path="blogs" element={<AdminBlogs />} />
        <Route path="resumes" element={<AdminResumes />} />
        <Route path="integrations" element={<AdminIntegrations />} />
        <Route path="site" element={<AdminSite />} />
        <Route path="slides" element={<AdminSlides />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="search-analytics" element={<AdminSearchAnalytics />} />
      </Route>

      <Route path="*" element={
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="font-display text-6xl font-extrabold text-emerald-400">404</h1>
          <p className="text-slate-400 mt-3">Page not found</p>
        </div>
      } />
    </Routes>
    </Suspense>
  );
}

function ChromeWrap({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      {!isAdmin && <Header />}
      <main className="flex-1">{children}</main>
      {!isAdmin && <Footer />}
    </>
  );
}

function AppInner() {
  useEffect(() => {
    AOS.init({ duration: 700, once: true, offset: 60, easing: "ease-out-cubic" });
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <ProcessingLoader />
        <SeoHead />
        <ChromeWrap>
          <AppRouter />
        </ChromeWrap>
      </BrowserRouter>
      <Toaster richColors position="top-right" theme="dark" />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <I18nProvider>
        <AuthProvider>
          <ThemeProvider>
            <AppInner />
          </ThemeProvider>
        </AuthProvider>
      </I18nProvider>
    </HelmetProvider>
  );
}

export default App;
